import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Row, Col, Spinner } from "react-bootstrap";
import { InfoCircle } from 'iconsax-react';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { ruoloConsiglio } from '../../../../components/Consiglio/ruoli';
import { AvvisoInLista } from '../../../../components/Util/AvvisoInLista';

const oggi = () => new Date().toISOString().substring(0, 10);
const mandatoScaduto = (membro) => !!membro.mandateEnd && String(membro.mandateEnd).substring(0, 10) < oggi();

const TESTO_NON_SOCIO = "Nel libro soci risulta dimesso o espulso: il consiglio direttivo è composto solo da soci. Rimuovilo dal consiglio o chiudi il suo mandato.";

const formatDate = (iso) => {
	if (!iso) return '—';
	const [anno, mese, giorno] = String(iso).substring(0, 10).split('-');
	if (!anno || !mese || !giorno) return iso;
	return `${giorno}/${mese}/${anno}`;
};

const iniziali = (nome) =>
	String(nome || '')
		.split(' ')
		.filter(Boolean)
		.slice(0, 2)
		.map(p => p[0])
		.join('')
		.toUpperCase();

export const CdaTable = () => {

	const navigate = useNavigate();

	const [membri, setMembri] = useState(null);
	const [loadingShowed, setLoadingShowed] = useState(false);

	const [modalShowed, setModalShowed] = useState(false);
	const [modalTitle, setModalTitle] = useState('');
	const [modalText, setModalText] = useState('');

	const getAuth = () => {
		const tokenDetailsString = sessionStorage.getItem('userDetails');
		if (!tokenDetailsString) return null;
		const tokenDetails = JSON.parse(tokenDetailsString);
		return { token: tokenDetails.idToken, clubId: tokenDetails.localId };
	};

	const mostraErrore = (testo) => {
		setModalTitle("Errore durante l'operazione");
		setModalText(testo);
		setModalShowed(true);
	};

	const carica = useCallback(async () => {
		const auth = getAuth();
		if (!auth) return;

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const risultato = await apiClient.getClubBoard(auth.token, auth.clubId);
		setLoadingShowed(false);

		if (!risultato || risultato.error) {
			setMembri([]);
			mostraErrore("Non è stato possibile caricare il consiglio direttivo: " + ((risultato && risultato.error) || 'errore generico'));
			return;
		}
		// Mandati scaduti in fondo, per il resto l'ordine del server
		const elenco = Array.isArray(risultato) ? risultato : [];
		setMembri([...elenco.filter(m => !mandatoScaduto(m)), ...elenco.filter(mandatoScaduto)]);
	}, []);

	useEffect(() => {
		carica();
	}, [carica]);

	// La riga apre la scheda del membro: «Firma», «Visualizza player», «Modifica» e «Rimuovi» stanno lì (03/10/2026)
	const apriScheda = (membro) => navigate('/struttura/cda/scheda/' + membro.membershipId);

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status">
						<span className="sr-only">Loading...</span>
					</Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<Modal className="fade" show={modalShowed} centered>
				<Modal.Header>
					<Modal.Title>{modalTitle}</Modal.Title>
					<Button onClick={() => setModalShowed(false)} variant="" className="btn-close"></Button>
				</Modal.Header>
				<Modal.Body>
					<Row><Col>{modalText}</Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-center">
					<Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>Chiudi</Button>
				</Modal.Footer>
			</Modal>

			<div className="sid-info-banner">
				<InfoCircle size="18" className="sid-info-icon" />
				<div>
					<strong>Il consiglio direttivo è composto esclusivamente da soci.</strong>
					<div className="mt-1">
						Puoi aggiungere come membri solo i player iscritti al{' '}
						<a href="/struttura/soci/list" style={{ color: '#E66A0F', fontWeight: 600 }}>Libro Soci</a> della Struttura.
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card-header">
					<h4 className="card-title">Membri del consiglio</h4>
					<div className="ml-auto d-flex align-items-center">
						<Button
							className="btn btn-primary btn-add"
							style={{ textTransform: 'none' }}
							onClick={() => navigate('/struttura/cda/scheda')}
						>
							+ Aggiungi membro
						</Button>
					</div>
				</div>
				<div className="card-body">
					<div className="table-responsive" style={{ overflowX: 'auto' }}>
						<table className="table dataTable display">
							<thead>
								<tr>
									<th style={{ width: '56px' }}></th>
									<th>Nome e Cognome</th>
									<th>Ruolo</th>
									<th>Inizio mandato</th>
									<th>Fine mandato</th>
									<th>Firma digitale</th>
									<th className="sid-col-arrow" aria-hidden="true" />
								</tr>
							</thead>
							<tbody>
								{membri === null && (
									<tr><td colSpan={7} style={{ textAlign: 'center' }}>Caricamento in corso</td></tr>
								)}
								{membri !== null && membri.length === 0 && (
									<tr><td colSpan={7} style={{ textAlign: 'center' }}>Nessun membro nel consiglio direttivo</td></tr>
								)}
								{(membri || []).map(membro => {
									const ruolo = ruoloConsiglio(membro.role);
									return (
										<tr
											key={membro.membershipId}
											className="sid-row-link"
											tabIndex={0}
											onClick={() => apriScheda(membro)}
											onKeyDown={(e) => { if (e.key === 'Enter') apriScheda(membro); }}
										>
											<td>
												<span className="sid-cd-avatar">
													{membro.picture && membro.picture.uri
														? <img src={membro.picture.uri} alt={membro.name} />
														: iniziali(membro.name)}
												</span>
											</td>
											<td style={{ fontWeight: 600 }}>
												{membro.name}
												{membro.isClubMember === false && <> <AvvisoInLista testo={TESTO_NON_SOCIO} /></>}
											</td>
											<td>{ruolo.label}</td>
											<td>{formatDate(membro.mandateStart)}</td>
											<td>
												{formatDate(membro.mandateEnd)}
												{mandatoScaduto(membro) && <div style={{ fontSize: '12px' }}>Mandato scaduto</div>}
											</td>
											<td>
												<div className="sid-firma-cell">
													<span
														className="sid-firma-dot"
														style={{ background: membro.hasSignature ? '#00A15D' : '#D4D7DD' }}
													/>
													{membro.hasSignature ? 'Presente' : 'Assente'}
												</div>
											</td>
											<td className="sid-col-arrow" aria-hidden="true"><i className="fa fa-chevron-right" /></td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</>
	);
};

export default CdaTable;
